import { apiClient } from "@/lib/api-client"
import type {
  IntentionCommunitiesResponse,
  IntentionDetail,
  IntentionFiltersState,
  IntentionRitualsResponse,
  IntentionSessionsFilters,
  IntentionSessionsResponse,
  IntentionUsersResponse,
} from "../types"

export async function getIntention(slug: string): Promise<IntentionDetail> {
  const response = await apiClient.get<{
    success: boolean
    data: IntentionDetail
    message: string
  }>(`/api/intentions/${slug}`)
  return response.data.data
}

export async function getIntentionRituals(
  slug: string,
  filters: IntentionFiltersState,
  page: number,
  limit = 8,
): Promise<IntentionRitualsResponse> {
  const params = new URLSearchParams()
  params.set("page", String(page))
  params.set("limit", String(limit))

  if (filters.search) params.set("search", filters.search)
  if (filters.sort !== "newest") params.set("sort", filters.sort)
  if (filters.price !== "all") params.set("price", filters.price)
  if (filters.format !== "all") params.set("format", filters.format)
  if (filters.duration !== "all") params.set("duration", filters.duration)
  if (filters.city) params.set("city", filters.city)
  if (filters.state) params.set("state", filters.state)
  if (filters.country) params.set("country", filters.country)

  const response = await apiClient.get<{
    success: boolean
    data: IntentionRitualsResponse
    message: string
  }>(`/api/intentions/${slug}/rituals?${params.toString()}`)
  return response.data.data
}

export async function toggleIntention(
  intentionId: string,
): Promise<{ action: "added" | "removed" }> {
  const response = await apiClient.post<{
    success: boolean
    data: { action: "added" | "removed" }
    message: string
  }>(`/api/intentions/${intentionId}/toggle`)
  return response.data.data
}

export interface SearchIntentionsResult {
  intentions: Array<{
    id: string
    name: string
    slug: string | null
    description: string
  }>
  total: number
  page: number
  totalPages: number
}

export async function searchIntentions(
  query: string,
  page: number = 1,
  limit: number = 20,
): Promise<SearchIntentionsResult> {
  const params = new URLSearchParams()
  if (query) params.set("query", query)
  params.set("page", String(page))
  params.set("limit", String(limit))

  const response = await apiClient.get<{
    success: boolean
    data: SearchIntentionsResult
    message: string
  }>(`/api/intentions/search?${params.toString()}`)
  return response.data.data
}

// ── Tab endpoints ────────────────────────────────────

export async function getIntentionSessions(
  slug: string,
  filters: IntentionSessionsFilters,
  page: number,
  limit = 8,
): Promise<IntentionSessionsResponse> {
  const params = new URLSearchParams()
  params.set("page", String(page))
  params.set("limit", String(limit))
  if (filters.search) params.set("search", filters.search)
  if (filters.sort) params.set("sort", filters.sort)
  if (filters.spiritualTradition) params.set("spiritualTradition", filters.spiritualTradition)
  if (filters.duration && filters.duration !== "all") params.set("duration", filters.duration)

  const response = await apiClient.get<{
    success: boolean
    data: IntentionSessionsResponse
    message: string
  }>(`/api/intentions/${slug}/sessions?${params.toString()}`)
  return response.data.data
}

export async function getIntentionCommunities(
  slug: string,
  filters: { sort?: string; search?: string; spiritualTradition?: string },
  page: number,
  limit = 8,
): Promise<IntentionCommunitiesResponse> {
  const params = new URLSearchParams()
  params.set("page", String(page))
  params.set("limit", String(limit))
  if (filters.search) params.set("search", filters.search)
  if (filters.sort) params.set("sort", filters.sort)
  if (filters.spiritualTradition) params.set("spiritualTradition", filters.spiritualTradition)

  const response = await apiClient.get<{
    success: boolean
    data: IntentionCommunitiesResponse
    message: string
  }>(`/api/intentions/${slug}/community?${params.toString()}`)
  return response.data.data
}

export async function getIntentionMembers(
  slug: string,
  filters: { sort?: string; search?: string; spiritualTradition?: string },
  page: number,
  limit = 8,
): Promise<IntentionUsersResponse> {
  const params = new URLSearchParams()
  params.set("page", String(page))
  params.set("limit", String(limit))
  if (filters.search) params.set("search", filters.search)
  if (filters.sort) params.set("sort", filters.sort)
  if (filters.spiritualTradition) params.set("spiritualTradition", filters.spiritualTradition)

  const response = await apiClient.get<{
    success: boolean
    data: IntentionUsersResponse
    message: string
  }>(`/api/intentions/${slug}/members?${params.toString()}`)
  return response.data.data
}

export async function getIntentionExperts(
  slug: string,
  filters: { sort?: string; search?: string; spiritualTradition?: string },
  page: number,
  limit = 8,
): Promise<IntentionUsersResponse> {
  const params = new URLSearchParams()
  params.set("page", String(page))
  params.set("limit", String(limit))
  if (filters.search) params.set("search", filters.search)
  if (filters.sort) params.set("sort", filters.sort)
  if (filters.spiritualTradition) params.set("spiritualTradition", filters.spiritualTradition)

  const response = await apiClient.get<{
    success: boolean
    data: IntentionUsersResponse
    message: string
  }>(`/api/intentions/${slug}/experts?${params.toString()}`)
  return response.data.data
}

export async function getIntentionRitualists(
  slug: string,
  filters: { sort?: string; search?: string; spiritualTradition?: string },
  page: number,
  limit = 8,
): Promise<IntentionUsersResponse> {
  const params = new URLSearchParams()
  params.set("page", String(page))
  params.set("limit", String(limit))
  if (filters.search) params.set("search", filters.search)
  if (filters.sort) params.set("sort", filters.sort)
  if (filters.spiritualTradition) params.set("spiritualTradition", filters.spiritualTradition)

  const response = await apiClient.get<{
    success: boolean
    data: IntentionUsersResponse
    message: string
  }>(`/api/intentions/${slug}/ritualists?${params.toString()}`)
  return response.data.data
}
